// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module MetricCard
 * @package @geenius/analytics/react
 * @description Renders the React metric card for a single computed summary
 * metric. The card formats percentages, durations, and raw counts while
 * exposing trend coloring from the shared analytics tokens.
 */

import type { MetricSummary } from "@geenius/analytics";
import { Badge, Button, Card, CardContent } from "@geenius/ui/react";
import type React from "react";

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Props for rendering the React metric card.
 *
 * @property metric Metric summary payload rendered by the card.
 * @property error Optional metric-level error message or object.
 * @property onRetry Optional retry handler rendered when an error is present.
 * @property className Optional class name merged into the card wrapper.
 */
export interface MetricCardProps {
  metric: MetricSummary;
  error?: Error | string | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders a React metric card for one summary metric.
 *
 * @param props Summary metric payload for the card.
 * @returns A metric card element with formatted value and optional trend.
 */
export function MetricCard(props: MetricCardProps): React.ReactElement {
  const { metric, error, onRetry } = props;

  const trendVariant =
    metric.trend === "up"
      ? "success"
      : metric.trend === "down"
        ? "danger"
        : "secondary";
  const formatMetricValue = (value: number): string =>
    metric.format === "percent"
      ? `${value.toFixed(1)}%`
      : metric.format === "duration"
        ? `${(value / 1000).toFixed(1)}s`
        : value.toLocaleString();
  const formattedChange =
    metric.changePercent !== undefined
      ? `${metric.changePercent.toFixed(1)}%`
      : metric.change !== undefined
        ? formatMetricValue(metric.change)
        : null;

  if (error) {
    return (
      <Card className={props.className}>
        <CardContent className="pt-6">
          <div className="grid gap-3" role="alert">
            <span className="text-sm font-medium text-foreground">
              {metric.label}
            </span>
            <span className="text-sm text-muted-foreground">
              {getErrorMessage(error)}
            </span>
            {onRetry && (
              <Button
                className="w-fit"
                type="button"
                variant="secondary"
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={props.className}>
      <CardContent className="flex flex-col gap-3 pt-6">
        <span
          className="text-sm text-muted-foreground"
          data-analytics="metric-label"
        >
          {metric.label}
        </span>
        <span
          className="text-2xl font-semibold tracking-tight"
          data-analytics="metric-value"
        >
          {formatMetricValue(metric.value)}
        </span>
        {formattedChange && (
          <Badge variant={trendVariant} size="sm">
            <span data-analytics="metric-change" data-trend={metric.trend}>
              {metric.trend === "up"
                ? "↑"
                : metric.trend === "down"
                  ? "↓"
                  : "→"}{" "}
              {formattedChange}
            </span>
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}
